import {
  onCleanup,
  createEffect,
  createRoot,
  createSignal,
  Show,
  For,
  on,
  createSelector,
  createMemo,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { insert } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import {
  register,
  compose,
} from "https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm";

import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner)
    return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (
    next &&
    !next._$owner &&
    !(next.assignedSlot && next.assignedSlot._$owner)
  )
    next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = "";
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (
  tagName,
  props = {},
  Component = (props, options) => {},
  ...rest
) =>
  compose(
    register(tagName, props),
    withNoShadowDOM,
    withSolid,
    ...rest,
  )(Component);

const srOnly = `border-width:0 !important;clip:rect(1px, 1px, 1px, 1px) !important;-webkit-clip-path:inset(50%) !important;clip-path:inset(50%) !important;height:1px !important;margin-top:-1px !important;margin-bottom:-1px !important;margin-right:-1px !important;margin-left:-1px !important;overflow:hidden !important;padding-top:0 !important;padding-bottom:0 !important;padding-right:0 !important;padding-left:0 !important;position:absolute !important;width:1px !important;white-space:nowrap !important;`;

function addHiddenStyles(element) {
  element.style.setProperty("visibility", "hidden", "important");
  element.style.setProperty("opacity", "0", "important");
}

function addVisibleStyles(element) {
  element.style.display = null;
  element.style.visibility = null;
  element.style.opacity = null;
}

function initializeClasses(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.classList.add("keen-dots");
    addHiddenStyles(element);
    return ComponentType(props, options);
  };
}

function getMaxSlides(slider) {
  const perView = slider.options?.slides.perView || 1,
    perScroll = slider.options?.slides.perScroll || 1,
    result = Math.ceil((slider.slides.length - perView) / perScroll) + 1;
  if (perScroll === 1 && perView > 1) return slider.slides.length;

  return result;
}
function constrain(value, min, max) {
  return Math.max(Math.min(value, max), min);
}

function checkLoaded() {
  return (
    document.readyState === "complete" ||
    document.readyState === "loaded" ||
    document.readyState === "interactive"
  );
}

function Component(props, { element }) {
  const [slider, setSlider] = createSignal(null);
  const [maxIdx, setMaxIdx] = createSignal(0);
  const [currentSlide, setCurrentSlide] = createSignal(0);

  const maxSlides = createMemo(
    on(maxIdx, (maxIdx) => {
      if (!maxIdx) return 0;
      return getMaxSlides(slider());
    }),
  );

  createEffect(
    on(
      () => props.target,
      (target) => {
        let targetEl = element;
        if (target) targetEl = document.querySelector(target);
        if (targetEl.tagName !== "KEEN-SLIDER")
          targetEl = targetEl.querySelector("keen-slider");
        if (!targetEl) return;

        let unsubscribe = () => null;

        function handleSubscribe() {
          unsubscribe = targetEl.subscribe((slider) => {
            setSlider(slider);
            setMaxIdx(slider.track.details.maxIdx);
          });
        }

        if (checkLoaded()) return setTimeout(() => handleSubscribe());

        window.addEventListener("load", handleSubscribe);

        return onCleanup(() => {
          window.removeEventListener("load", handleSubscribe);
          unsubscribe();
        });
      },
    ),
  );

  createEffect(
    on(slider, (slider) => {
      if (!slider) return;

      function handleDetailsChange(slider) {
        setMaxIdx(slider.track.details.maxIdx);
      }

      function handleSlideChange(slider) {
        setCurrentSlide(getSlideIndex(slider));
      }

      function handleSlideDestroy() {
        setCurrentSlide(0);
        setMaxIdx(0);
      }

      slider.on("detailsChanged", handleDetailsChange);
      slider.on("slideChanged", handleSlideChange);
      slider.on("destroyed", handleSlideDestroy);

      return onCleanup(() => {
        slider.on("detailsChanged", handleDetailsChange, true);
        slider.on("slideChanged", handleSlideChange, true);
        slider.on("destroyed", handleSlideDestroy, true);
      });
    }),
  );

  createEffect(
    on(maxSlides, (maxSlides) => {
      if (maxSlides <= 1) return addHiddenStyles(element);
      return addVisibleStyles(element);
    }),
  );

  const isSelected = createSelector(currentSlide);

  function getSlideIndex(s, idx) {
    const i = typeof idx === "number" ? idx : s.track.details.rel;
    return constrain(
      i * (s?.options?.slides?.perScroll || 1),
      0,
      s.track.details.maxIdx,
    );
  }

  return html`
    <${Show} when=${() => maxSlides() > 1}>
      <ul class="keen-dots" role="tablist">
        <${For} each=${() => Array(maxSlides()).fill(0)}>
          ${(_, idx) => {
            return html`
              <li
                class=${() =>
                  `keen-dot ${isSelected(idx()) ? "keen-dot--active" : ""}`}
                role="presentation"
              >
                <button
                  onClick=${(e) => {
                    const s = slider();
                    const i = getSlideIndex(s, idx());
                    s.moveToIdx(i);
                  }}
                  disabled=${() => isSelected(idx())}
                  class=${() => `keen-dot__button`}
                  aria-current=${() => isSelected(idx())}
                >
                  <span style=${srOnly}>Go to slide ${() => idx() + 1}</span>
                </button>
              </li>
            `;
          }}
        <//>
      </ul>
    <//>
  `;
}

customShadowlessElement(
  "keen-navigation-dots",
  { target: "" },
  Component,
  initializeClasses,
);
